import { useEffect, useRef } from "react";
import { animate, stagger, svg, utils } from "animejs";
import useAnimeScope from "../../hooks/useAnimeScope";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import "./Atmosphere.scss";

// Click effect options: "burst", "constellation", "ripple"
const CLICK_EFFECT = "burst";

const nodes = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: `${utils.random(4, 96)}%`,
  top: `${utils.random(8, 92)}%`,
  size: utils.random(3, 9)
}));

const shouldIgnoreClick = (target) => {
  if (!target?.closest) return false;

  return Boolean(
    target.closest(
      [
        "a",
        "button",
        "input",
        "textarea",
        "select",
        "label",
        "[role='button']",
        ".navigation",
        ".hero__profile-card",
        ".projects__card",
        ".about-card",
        ".skill-card",
        ".contact__form-panel",
        ".contact__social"
      ].join(",")
    )
  );
};

const buildBurst = (root, x, y) => {
  const wrap = document.createElement("div");
  wrap.className = "atmosphere__click-burst";
  root.appendChild(wrap);

  const sparks = Array.from({ length: 16 }, (_, index) => {
    const spark = document.createElement("span");
    const angle = (Math.PI * 2 * index) / 16 + utils.random(-18, 18) * (Math.PI / 180);
    const distance = utils.random(34, 118);

    spark.className = "atmosphere__spark";
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    spark.style.setProperty("--dx", `${Math.cos(angle) * distance}px`);
    spark.style.setProperty("--dy", `${Math.sin(angle) * distance}px`);
    spark.style.setProperty("--s", `${utils.random(4, 9)}px`);
    spark.style.setProperty("--delay", `${index * 8}ms`);
    wrap.appendChild(spark);
    return spark;
  });

  animate(sparks, {
    translateX: (_, i) => sparks[i].style.getPropertyValue("--dx"),
    translateY: (_, i) => sparks[i].style.getPropertyValue("--dy"),
    scale: [1, 0.15],
    opacity: [0.95, 0],
    duration: () => utils.random(560, 960),
    delay: stagger(8),
    ease: "outExpo"
  });

  setTimeout(() => wrap.remove(), 1050);
};

const buildRipple = (root, x, y) => {
  const wrap = document.createElement("div");
  wrap.className = "atmosphere__click-burst";
  root.appendChild(wrap);

  const rings = Array.from({ length: 3 }, (_, index) => {
    const ring = document.createElement("span");
    ring.className = "atmosphere__ripple";
    ring.style.left = `${x}px`;
    ring.style.top = `${y}px`;
    ring.style.setProperty("--size", `${84 + index * 58}px`);
    wrap.appendChild(ring);
    return ring;
  });

  animate(rings, {
    scale: [0.12, 1],
    opacity: [0.42, 0],
    duration: 1050,
    delay: stagger(115),
    ease: "outQuart"
  });

  setTimeout(() => wrap.remove(), 1450);
};

const buildConstellation = (root, x, y) => {
  const wrap = document.createElement("div");
  wrap.className = "atmosphere__click-burst atmosphere__click-burst--constellation";
  root.appendChild(wrap);

  const points = Array.from({ length: 7 }, (_, index) => {
    const point = document.createElement("span");
    const px = x + utils.random(-96, 96);
    const py = y + utils.random(-70, 70);
    point.className = "atmosphere__constellation-point";
    point.style.left = `${px}px`;
    point.style.top = `${py}px`;
    point.dataset.x = String(px);
    point.dataset.y = String(py);
    wrap.appendChild(point);
    return point;
  });

  const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svgEl.setAttribute("class", "atmosphere__constellation-lines");
  svgEl.setAttribute("viewBox", `0 0 ${window.innerWidth} ${window.innerHeight}`);
  wrap.appendChild(svgEl);

  points.slice(1).forEach((point) => {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", points[0].dataset.x);
    line.setAttribute("y1", points[0].dataset.y);
    line.setAttribute("x2", point.dataset.x);
    line.setAttribute("y2", point.dataset.y);
    svgEl.appendChild(line);
  });

  animate(points, {
    scale: [0, 1, 0.2],
    opacity: [0, 1, 0],
    duration: 920,
    delay: stagger(32),
    ease: "outExpo"
  });

  animate(svgEl.querySelectorAll("line"), {
    opacity: [0, 0.46, 0],
    duration: 920,
    delay: stagger(42),
    ease: "inOutSine"
  });

  setTimeout(() => wrap.remove(), 1200);
};

const Atmosphere = () => {
  const rootRef = useRef(null);
  const reduceMotion = usePrefersReducedMotion();

  useAnimeScope(rootRef, () => {
    if (reduceMotion) return undefined;

    animate(".atmosphere__node", {
      translateX: () => utils.random(-26, 26),
      translateY: () => utils.random(-22, 22),
      scale: [
        { to: 1.35, duration: 1600 },
        { to: 0.85, duration: 1800 }
      ],
      opacity: [
        { to: 0.72, duration: 1200 },
        { to: 0.26, duration: 1800 }
      ],
      delay: stagger(140, { from: "random" }),
      alternate: true,
      loop: true,
      ease: "inOutSine"
    });

    animate(svg.createDrawable(".atmosphere__beam"), {
      draw: ["0 0", "0 1", "1 1"],
      duration: 5200,
      delay: stagger(650),
      loop: true,
      ease: "inOutQuad"
    });

    return undefined;
  }, [reduceMotion]);

  useEffect(() => {
    if (reduceMotion) return undefined;

    const handleClick = (event) => {
      const root = rootRef.current;
      if (!root || shouldIgnoreClick(event.target)) return;

      const { clientX, clientY } = event;

      if (CLICK_EFFECT === "ripple") {
        buildRipple(root, clientX, clientY);
        return;
      }

      if (CLICK_EFFECT === "constellation") {
        buildConstellation(root, clientX, clientY);
        return;
      }

      buildBurst(root, clientX, clientY);
    };

    window.addEventListener("click", handleClick, { passive: true });
    return () => window.removeEventListener("click", handleClick);
  }, [reduceMotion]);

  return (
    <div ref={rootRef} className="atmosphere" aria-hidden="true">
      <svg className="atmosphere__map" viewBox="0 0 1440 900" preserveAspectRatio="none">
        <path className="atmosphere__beam" d="M80 180 C280 80 430 240 620 130 S1040 90 1300 240" />
        <path className="atmosphere__beam" d="M140 720 C360 520 540 850 760 620 S1120 560 1360 700" />
        <path className="atmosphere__beam" d="M0 420 C260 340 400 500 620 420 S980 320 1440 420" />
      </svg>

      <div className="atmosphere__mesh">
        {nodes.map((node) => (
          <span
            key={node.id}
            className="atmosphere__node"
            style={{
              left: node.left,
              top: node.top,
              width: node.size,
              height: node.size
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default Atmosphere;
